import {memo} from "react";
import {Table, Tag, Tooltip} from "antd";
import {QuestionCircleOutlined} from "@ant-design/icons";
import {
	Image,
	PopperUserInfo
} from "~/components";
import {numberFormat} from "~/utils";

//Ngưỡng mẫu tối thiểu để hiển thị %; dưới ngưỡng trả về '—' tránh tỷ lệ ảo
function percent(part, total, minTotal = 1) {
	if(!total || total < minTotal) return '—';
	return Math.round((part / total) * 100) + '%';
}

//Tỉ lệ chốt dạng tỉ số: số cuộc tư vấn cho 1 hợp đồng, vd "4:1"
function closeRatio(consultations, contracts) {
	if(!contracts) return '—';
	const x = Math.round((consultations / contracts) * 10) / 10;
	return `${x}:1`;
}

//Dòng nhỏ so sánh tăng/giảm so với cùng kỳ tháng trước
function deltaEl(cur, prev) {
	cur = cur || 0;
	prev = prev || 0;
	let text, color;
	if(prev === 0) {
		if(cur === 0) { text = '—'; color = '#9ca3af'; }
		else { text = 'Mới'; color = '#16a34a'; }
	} else {
		const pct = Math.round(((cur - prev) / prev) * 100);
		if(pct === 0) { text = '0%'; color = '#9ca3af'; }
		else if(pct > 0) { text = `▲ ${pct}%`; color = '#16a34a'; }
		else { text = `▼ ${Math.abs(pct)}%`; color = '#dc2626'; }
	}
	return <Tooltip title={`Kỳ trước: ${numberFormat(prev)}`}><div style={{fontSize: '10.5px', color}}>{text}</div></Tooltip>;
}

//Tiêu đề cột kèm tooltip giải thích
function header(label, tip) {
	return (
		<Tooltip title={tip}>
			<span>{label} <QuestionCircleOutlined style={{fontSize: '11px', color: '#9ca3af'}} /></span>
		</Tooltip>
	);
}

function StaffSummaryTable({staff = [], total = {}, onCellClick}) {

	const perContract = (item) => (item.contracts ? item.revenue / item.contracts : 0);

	const openDetail = (item, type, typeLabel) => {
		if(!onCellClick) return;
		const name = `${item?.user?.firstname || ''} ${item?.user?.lastname || ''}`.trim() || item?.user?.username;
		onCellClick({userId: item?.user?.id, userName: name, type, typeLabel});
	};

	//Bọc giá trị thành link click mở modal chi tiết (chỉ khi có số liệu)
	const clickLink = (content, item, type, label, enabled = true) => {
		if(!onCellClick || !enabled) return content;
		return (
			<span style={{cursor: 'pointer', borderBottom: '1px dashed #93c5fd'}} onClick={() => openDetail(item, type, label)}>
				{content}
			</span>
		);
	};

	const columns = [
		{ title: '#', key: 'rank', width: '50px', align: 'center', fixed: 'left', render: (_, __, index) => (
			<b className="color-gray">{index + 1}</b>
		)},
		{ title: 'Nhân viên', dataIndex: 'user', key: 'user', fixed: 'left', render: (_, item) => (
			<PopperUserInfo user={item.user}>
				<div className="d-flex gap align-items">
					<div className="avatar"><Image src={item?.user?.avatar} /></div>
					<div>
						<b>{`${item?.user?.firstname || ''} ${item?.user?.lastname || ''}`}</b>
						<p>{item?.user?.username}</p>
					</div>
				</div>
			</PopperUserInfo>
		)},
		{ title: 'Công ty', dataIndex: 'company', key: 'company', width: '110px', render: (_, item) => (
			item?.user?.company?.label ? <Tag color={item.user.company.badge}>{item.user.company.label}</Tag> : <span className="color-gray">—</span>
		)},
		{ title: 'Chức vụ', dataIndex: 'role', key: 'role', width: '130px', render: (_, item) => (
			<span>{item?.user?.role?.label || '—'}</span>
		)},
		{ title: 'Nhóm', dataIndex: 'groupName', key: 'groupName', width: '150px', render: (_, item) => (
			<span>{item?.groupName || <span className="color-gray">Chưa phân nhóm</span>}</span>
		)},
		{ title: header('Nguồn', 'Số nguồn (liên hệ) tìm được trong kỳ — theo ngày tạo. Dòng nhỏ ▲/▼% = so với cùng kỳ tháng trước.'), dataIndex: 'sources', key: 'sources', width: '110px', align: 'center',
			sorter: (a, b) => a.sources - b.sources, render: (_, item) => (
			<div>
				{clickLink(<b className="text-md">{item.sources}</b>, item, 'sources', 'Nguồn tìm được', item.sources > 0)}
				{deltaEl(item.sources, item.prev?.sources)}
			</div>
		)},
		{ title: header('Cuộc gọi', 'Số lần gọi trong kỳ. % = Cuộc gọi ÷ Nguồn (ẩn "—" khi Nguồn < 5). Dòng nhỏ ▲/▼% = so với kỳ trước.'), dataIndex: 'calls', key: 'calls', width: '130px', align: 'center',
			sorter: (a, b) => a.calls - b.calls, render: (_, item) => (
			<div>
				{clickLink(<span>{item.calls}</span>, item, 'calls', 'Cuộc gọi', item.calls > 0)} <span className="color-gray">({percent(item.calls, item.sources, 5)})</span>
				{deltaEl(item.calls, item.prev?.calls)}
			</div>
		)},
		{ title: header('Hẹn', 'Số lịch hẹn tư vấn được tạo — theo ngày tạo. % = Hẹn ÷ Cuộc gọi (ẩn "—" khi Cuộc gọi < 5). Dòng nhỏ ▲/▼% = so với kỳ trước.'), dataIndex: 'appointments', key: 'appointments', width: '130px', align: 'center',
			sorter: (a, b) => a.appointments - b.appointments, render: (_, item) => (
			<div>
				{clickLink(<span>{item.appointments}</span>, item, 'appointments', 'Cuộc hẹn', item.appointments > 0)} <span className="color-gray">({percent(item.appointments, item.calls, 5)})</span>
				{deltaEl(item.appointments, item.prev?.appointments)}
			</div>
		)},
		{ title: header('Tư vấn', 'Số buổi tư vấn đã hoàn thành — tính cho người tư vấn. % = Tư vấn ÷ Hẹn (ẩn "—" khi Hẹn < 3). Dòng nhỏ ▲/▼% = so với kỳ trước.'), dataIndex: 'consultations', key: 'consultations', width: '130px', align: 'center',
			sorter: (a, b) => a.consultations - b.consultations, render: (_, item) => (
			<div>
				{clickLink(<b className="color-green">{item.consultations}</b>, item, 'consultations', 'Cuộc tư vấn hoàn thành', item.consultations > 0)} <span className="color-gray">({percent(item.consultations, item.appointments, 3)})</span>
				{deltaEl(item.consultations, item.prev?.consultations)}
			</div>
		)},
		{ title: header('Hủy hẹn', 'Số lịch hẹn bị hủy. % = Hủy ÷ Hẹn.'), dataIndex: 'cancels', key: 'cancels', width: '110px', align: 'center',
			sorter: (a, b) => a.cancels - b.cancels, render: (_, item) => (
			<span className={item.cancels > 0 ? 'color-red' : ''}>
				{item.cancels} <span className="color-gray">({percent(item.cancels, item.appointments, 3)})</span>
			</span>
		)},
		{ title: header('Hợp đồng', 'Số hợp đồng mới ký (mới / nâng cấp mới), trừ tên miền — theo ngày ký. Host + web cùng khách & cùng tháng tính là 1; web giá trị 0 không tính. Tỉ số trong ngoặc = số cuộc tư vấn cho 1 hợp đồng (vd 4:1 = 4 tư vấn được 1 HĐ). Dòng nhỏ ▲/▼% = so với kỳ trước.'), dataIndex: 'contracts', key: 'contracts', width: '140px', align: 'center',
			sorter: (a, b) => a.contracts - b.contracts, render: (_, item) => (
			<div>
				{clickLink(<b>{item.contracts}</b>, item, 'contracts', 'Hợp đồng', item.contracts > 0)} <span className="color-gray">({closeRatio(item.consultations, item.contracts)})</span>
				{deltaEl(item.contracts, item.prev?.contracts)}
			</div>
		)},
		{ title: header('DT TB/HĐ', 'Doanh thu trung bình mỗi hợp đồng = Doanh thu ÷ Số hợp đồng.'), dataIndex: 'perContract', key: 'perContract', width: '150px', align: 'right',
			sorter: (a, b) => perContract(a) - perContract(b), render: (_, item) => (
			<span>{numberFormat(perContract(item))} đ</span>
		)},
		{ title: header('Doanh thu', 'Doanh thu thực thu đã duyệt (trừ tên miền). Dòng nhỏ ▲/▼% = so với cùng kỳ tháng trước.'), dataIndex: 'revenue', key: 'revenue', width: '180px', align: 'right', fixed: 'right',
			sorter: (a, b) => a.revenue - b.revenue, render: (_, item) => (
			<div>
				{clickLink(<b className="text-md">{numberFormat(item.revenue)} đ</b>, item, 'revenue', 'Doanh thu', item.revenue > 0)}
				{deltaEl(item.revenue, item.prev?.revenue)}
			</div>
		)},
	];

	return (
		<div className="table-box">
			<Table
				columns={columns}
				rowKey={(row) => row.user?.id}
				dataSource={staff}
				scroll={{ x: 'max-content' }}
				pagination={false}
				summary={() => (
					<Table.Summary fixed>
						<Table.Summary.Row>
							<Table.Summary.Cell index={0} align="center" />
							<Table.Summary.Cell index={1}><b>Tổng cộng</b></Table.Summary.Cell>
							<Table.Summary.Cell index={2} />
							<Table.Summary.Cell index={3} />
							<Table.Summary.Cell index={4} />
							<Table.Summary.Cell index={5} align="center"><b>{total.sources || 0}</b></Table.Summary.Cell>
							<Table.Summary.Cell index={6} align="center"><b>{total.calls || 0}</b></Table.Summary.Cell>
							<Table.Summary.Cell index={7} align="center"><b>{total.appointments || 0}</b></Table.Summary.Cell>
							<Table.Summary.Cell index={8} align="center"><b className="color-green">{total.consultations || 0}</b></Table.Summary.Cell>
							<Table.Summary.Cell index={9} align="center"><b>{total.cancels || 0}</b></Table.Summary.Cell>
							<Table.Summary.Cell index={10} align="center"><b>{total.contracts || 0}</b></Table.Summary.Cell>
							<Table.Summary.Cell index={11} align="right"><b>{numberFormat(total.contracts ? (total.revenue || 0) / total.contracts : 0)} đ</b></Table.Summary.Cell>
							<Table.Summary.Cell index={12} align="right"><b>{numberFormat(total.revenue || 0)} đ</b></Table.Summary.Cell>
						</Table.Summary.Row>
					</Table.Summary>
				)}
			/>
		</div>
	)
}

export default memo(StaffSummaryTable);
